/**
 * The task-dispatch write gate, in one place.
 *
 * Extracted verbatim from `TasksScreen` so that every surface that can
 * dispatch a task — the Tasks screen's inline form and modal, and the
 * Overview map's click-a-zone → Clean flow — answers "may this person
 * dispatch right now?" identically. Three independent conditions, and
 * failing ANY of them disables dispatch:
 *
 *   1. the `taskDispatchEnabled` feature flag (runtime config),
 *   2. backend readiness — `unreachable` and `degraded` both pause
 *      dispatch until the backend recovers (`unknown` deliberately
 *      does NOT: it is the boot state, and blocking on it would hide
 *      the form for the first seconds of every session),
 *   3. the viewer's highest role is in `TASK_DISPATCH_ROLES`
 *      (`src/auth/types.ts`, mirroring the backend's authoritative
 *      `policy/capabilities.py` — this gate only decides whether the
 *      controls are drawn; a mismatch surfaces as a 403).
 *
 * `roleAllowed` is returned separately from `writeDisabled`, and the
 * flag/readiness inputs are echoed back, so a caller can say WHY
 * dispatch is unavailable (the Tasks screen's banner renders different
 * copy per cause) rather than just hiding the button.
 */
import { useHighestRole } from '../../auth/useAuth';
import { useBackendStatus } from '../../state/useBackendStatus';
import { getRuntimeConfig } from '../../config/runtime-config';
import { TASK_DISPATCH_ROLES, type RoleToken } from '../../auth/types';
import type { BackendReadiness } from '../../state/backend-status';

export interface TaskDispatchGate {
  /** True when dispatch must not be offered, for any of the three
   *  reasons above. The single value most callers gate on. */
  writeDisabled: boolean;
  /** The role check alone — separate so a caller can distinguish "you
   *  may not" from "nobody can right now" in what it tells the user. */
  roleAllowed: boolean;
  /** The viewer's highest role (null when unauthenticated), for banner
   *  copy and for narrower per-screen checks layered on top (e.g.
   *  `MAINTENANCE_MANAGE_ROLES` on the Tasks calendar). */
  role: RoleToken | null;
  /** Echo of `featureFlags.taskDispatchEnabled`, for "why" copy. */
  dispatchFlagEnabled: boolean;
  /** Echo of the backend readiness that fed the gate, for "why" copy. */
  readiness: BackendReadiness;
  readinessDetail: string | null;
}

export function useTaskDispatchGate(): TaskDispatchGate {
  const flags = getRuntimeConfig().featureFlags;
  const role = useHighestRole();
  const backend = useBackendStatus();
  const roleAllowed = role !== null && TASK_DISPATCH_ROLES.includes(role);
  const writeDisabled =
    !flags.taskDispatchEnabled ||
    backend.status.readiness === 'unreachable' ||
    backend.status.readiness === 'degraded' ||
    !roleAllowed;
  return {
    writeDisabled,
    roleAllowed,
    role,
    dispatchFlagEnabled: flags.taskDispatchEnabled,
    readiness: backend.status.readiness,
    readinessDetail: backend.status.readinessDetail,
  };
}
